import {useEffect, useState} from 'react';

import {SparkAreaChart, Badge} from '@tremor/react';

import {parseFinancial} from 'utils/Financials';

import {DashboardStatData} from 'components/v2/app/dashboard/models';
import {CarouselPaginator} from 'components/v2/CarouselPaginator';

function sortDataByYearMonth(jsonData: any) {
  if (jsonData) {
    const data = Object.values(jsonData);

    // Sort the array based on the 'key' attribute (year-month), in descending order
    data.sort((a: any, b: any) => {
      // Split the keys to separate year and month, then compare them
      const [yearA, monthA] = a.key.split('-').map(Number);
      const [yearB, monthB] = b.key.split('-').map(Number);

      // First compare the years, then the months if the years are equal
      if (yearA !== yearB) {
        return yearB - yearA;
      }
      return monthB - monthA;
    });

    // Return the sorted array
    return data;
  } else {
    return [];
  }
}

export const OverviewTotalRevenue = ({stats}: {stats: DashboardStatData}) => {
  const [currentYear, setCurrentYear] = useState<string>(
    new Date().getFullYear().toString(),
  );
  const sortedYears = Object.keys(stats?.year || {}).sort((a, b) =>
    a.localeCompare(b),
  );
  const [currentYearIndex, setCurrentYearIndex] = useState<number>(0);

  useEffect(() => {
    if (stats.year) {
      const index = sortedYears.findIndex(year => year === currentYear);
      setCurrentYearIndex(index);
    }
  }, [currentYear, sortedYears, stats.year]);

  const changeYear = (next: boolean) => {
    const newIndex = next ? currentYearIndex + 1 : currentYearIndex - 1;
    if (sortedYears[newIndex]) {
      setCurrentYearIndex(newIndex);
      setCurrentYear(sortedYears[newIndex]);
    }
  };

  const orderMonthsAndYears = sortDataByYearMonth(stats.month);

  const monthsOfTheYear = [];

  for (let i = 1; i <= 12; i++) {
    const key = `${currentYear}-${i >= 10 ? i : `0${i}`}`;
    const findData = orderMonthsAndYears.find((i: any) => i.key === key);
    if (findData) {
      monthsOfTheYear.push(findData);
    } else {
      monthsOfTheYear.push('N/A');
    }
  }

  const monthsLabels = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const isChartEmpty = monthsOfTheYear.every(i => i === 'N/A');

  return (
    <>
      <div className={'flex flex-col gap-4 p-[16px_16px] h-full'}>
        <div className={'flex gap-2'}>
          <h3 className="text-[16px] font-bold">Total revenue</h3>
          {isChartEmpty && (
            <Badge
              size="xs"
              className={'bg-nr-gray-50 text-black ring-nr-gray-100'}>
              No data
            </Badge>
          )}
        </div>
        <div className={'h-full'}>
          <SparkAreaChart
            data={monthsOfTheYear.map((content: any, i: number) => {
              const month = monthsLabels[i];
              return {
                month,
                revenue: content === 'N/A' ? 10 : Math.round(content.revenue),
              };
            })}
            noDataText={'No data'}
            categories={['revenue']}
            index={'month'}
            colors={[isChartEmpty ? 'nr-gray-50' : 'emerald']}
            className={`h-8 w-20 sm:h-10 sm:w-36 !h-2/3 ${
              isChartEmpty && 'opacity-25'
            }`}
          />
        </div>
        <div className={'flex flex-col gap-3'}>
          <h2 className="my-auto text-5xl font-normal leading-9">
            $
            {parseFinancial(
              (stats.year &&
                stats.year[sortedYears[currentYearIndex]]?.revenue) ||
                0,
            )}
          </h2>
          <CarouselPaginator
            label={currentYear}
            current={currentYearIndex}
            min={0}
            max={sortedYears.length - 1}
            onBeforeClick={() => changeYear(false)}
            onAfterClick={() => changeYear(true)}
          />
        </div>
      </div>
    </>
  );
};
